import React, {useEffect, useState} from 'react';
import {Box, IconButton} from '@mui/material';
import {ArrowDropDown, ArrowRight} from '@mui/icons-material';

interface Props {
  data: Record<string, any>;
  autoExpand?: boolean;
}

const CollapsibleRenderReport: React.FC<Props> = (props: Props) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const {data} = props;

  useEffect(() => {
    if (data === null || typeof data !== 'object') {
      return;
    }
    const initialCollapsedState: Record<string, boolean> = {};
    const setInitialCollapsed = (data: Record<string, any>, parentKey: string = '') => {
      Object.entries(data).forEach(([key, value]) => {
        const currentKey = parentKey ? `${parentKey}.${key}` : key;
        if (typeof value === 'object' && value !== null && (Object.keys(value).length > 3 || !props.autoExpand)) {
          initialCollapsedState[currentKey] = true;
        }
        if (typeof value === 'object' && value !== null) {
          setInitialCollapsed(value, currentKey);
        }
      });
    };
    setInitialCollapsed(data);
    setCollapsed(initialCollapsedState);
  }, [data]);

  const toggleCollapse = (key: string) => {
    setCollapsed(prev => ({...prev, [key]: !prev[key]}));
  };

  const renderData = (data: Record<string, any>, level: number = 0, parentKey: string = '') => {
    if (typeof data !== 'object' || data === null) {
      return <span></span>;
    }
    return (
      <Box ml={level * 2}>
        {Object.entries(data).map(([key, value]) => {
          if (value === null) {
            return null;
          }
          const currentKey = parentKey ? `${parentKey}.${key}` : key;
          const numVals = Object.keys(value).length;
          const isValObj = typeof value === 'object'
          const isCollapsible = isValObj && numVals > 0;
          const isCollapsed = collapsed[currentKey];
          const itemCount = isCollapsible ? ` (${numVals})` : '';
          if (level === 0 && numVals === 0) {
            return (
              <Box key={currentKey} mb={1}>
                <span>{key}: {"No references found"}</span>
              </Box>
            );
          }

          return (
            <Box key={currentKey} mb={1}>
              <span>
                {isCollapsible && (
                  <IconButton size="small" onClick={() => toggleCollapse(currentKey)}>
                    {isCollapsed ? <ArrowRight/> : <ArrowDropDown/>}
                  </IconButton>
                )}
                {key}{itemCount}
              </span>
              {isCollapsible && !isCollapsed ? (
                renderData(value, level + 1, currentKey) ?? ''
              ) : (
                !isCollapsible && typeof value === 'string' && <span>{value ?? ''}</span>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      {renderData(data)}
    </Box>
  );
};

export default CollapsibleRenderReport;